import { Entry } from 'contentful'
import { Document } from '@contentful/rich-text-types'
import { IProductFields } from 'generated/types/contentful'
import { Fact, parseFact } from 'services/ContentfulService/models/fact'
import { Topic } from 'services/ContentfulService/models/topics'
import { Asset, parseAsset } from 'services/ContentfulService/models/asset'
import { Article } from './articles'
import { Partner } from './partner/partner'
import { $enum } from 'ts-enum-util'
import { FionaProducts } from 'parameters/general'
import { ContentfulEntity, parseSystemFields, removeFalsy } from 'services/ContentfulService/utils'

export interface Product extends ContentfulEntity {
	/** Name */
	name: string

	/** Icon */
	icon: Asset

	sort: number

	/** graphic */
	graphic: Asset

	/** Call To Action Text */
	ctaText: string

	/** Disclosure */
	disclosure?: string

	/** Subtitle */
	subtitle: string

	/** Even API Product Type */
	evenApiProductType: string

	/** Primary Content */
	primaryContent?: Document | undefined
	learnHub: boolean
	/** Parent */
	parent?: string
	crossSell: boolean
	shallow: boolean
	/** ID */
	id: string

	slug: string
	/** faqs */
	facts?: Fact[]

	/** PartnerNoun */
	partnerNoun: string
	relativeLearnPath: string
	relativeProductPage: string
}

export interface ProductWithChildren extends Product {
	children: Array<Product>
}
const defaultGraphic: Asset = { aspectRatio: 0, contentType: '', description: '', id: '', title: '', url: '' }

export const parseProduct = (rawProduct: Entry<IProductFields>, shallow = false): Product => {
	const fields = rawProduct.fields
	const parent = !shallow && fields.parent ? parseProduct(fields?.parent, true) : undefined
	return {
		learnHub: fields.learnHub || true,
		shallow,
		ctaText: fields.ctaText,
		disclosure: fields.disclosure,
		evenApiProductType: fields.evenApiProductType,
		facts: shallow ? undefined : removeFalsy((fields.faqs || [])?.map(fact => parseFact(fact))),
		graphic: fields.graphic ? parseAsset(fields.graphic) : parent?.graphic || defaultGraphic,
		icon: fields.icon ? parseAsset(fields.icon) : parent?.icon || defaultGraphic,
		slug: fields.slug || fields.id,
		id: rawProduct.sys.id,
		crossSell: fields.crossSell || false,
		name: fields.name!,
		parent: fields.parent?.sys.id,
		partnerNoun: fields.partnerNoun || 'partner',
		primaryContent: fields.primaryContent,
		subtitle: fields.subtitle,
		relativeLearnPath: parent?.relativeLearnPath || `/learn/products/${fields.slug}`,
		relativeProductPage: `/products/${fields.slug || fields.id}`,
		sort: fields.sort || 100,
		sys: parseSystemFields(rawProduct.sys)
	}
}

const ContentfulProductIds = {
	Mortgage: '5Dd85DsFxZaIRnL6EVNRkt',
	CreditCards: '3wK7mRnu5cC1emYeUQFp2D',
	Savings: '7BYDVYpz96D7KywGt7F1DQ',
	Insurance: '4Lk9mR6FsNmtfKpQMFxIIv',
	LifeInsurance: 'lJWpRY7UjuyjGtphtLLh2',
	SLR: '20a8ggfH0sT4Dgjkr5e7Wr',
	UnsecuredLoans: '4CL6dNftTmvXGOP5hcJJ3V',
	Loans: '4qEKR8VXyUbVU0oLsFZaL3'
}

export const LegacyProductLearnRedirects: { [x: string]: string } = {
	'student-loan-refinance': 'loans',
	'life-insurance': 'insurance',
	'auto-insurance': 'insurance'
}

export type ProductKeys = keyof typeof ContentfulProductIds

export const ContentfulTopicIds = {
	Auto_Refinance: '45SzjnaCrL8PLXlfPqQJ8G',
	Fiona: '5H0FbyzODH3QgnC8y5NVO2'
}

interface AnythingInterface {
	topics?: Topic[]
	products?: Product[]
	articles?: Article[]
	partners?: Partner[]
}

const isProductSlug = (slug: string) => $enum(FionaProducts).isValue(slug)
const toProductSlug = (slug: string): FionaProducts | undefined =>
	$enum(FionaProducts).asValueOrDefault(slug, undefined)

const getBestProductFromArray = (products: Product[]): FionaProducts | undefined => {
	const product = products
		.filter(p => isProductSlug(p.slug))
		.sort((a, b) => a.sort - b.sort)
		.shift()
	if (product) return toProductSlug(product.slug)
	return undefined
}

/**
 * Given any type of any data, choose the best product to show to the user
 * @param topics
 * @param articles
 * @param products
 * @param partners
 */
export const chooseAppropriateProduct = ({ topics, products }: AnythingInterface): FionaProducts => {
	let value = FionaProducts.Loans

	if (products) {
		const productValue = getBestProductFromArray(products)
		productValue && (value = productValue)
	}

	if (topics) {
		try {
			const productList = topics
				.filter(p => !p.shallow)
				.map(t => t.products || [])
				.reduce((accum, products) => [...accum, ...products], [])
			const best = getBestProductFromArray(productList)
			best && (value = best)
		} catch (e) {}
	}
	if (value === FionaProducts.Insurance) return FionaProducts.LifeInsurance
	return value
}

export const getTreeIds = (product: ProductWithChildren) => {
	return [...(product.children?.map(p => p.id) || []), product.id]
}
